import { useState } from "react";
import { toast } from "sonner";
import { Phone, Send, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { api, WA_LINK, WA_NUMBER, formatApiErrorDetail } from "@/lib/api";

const RAWDAH_IMG =
  "https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxtYXNqaWQlMjBuYWJhd2klMjBtYWRpbmFoJTIwcmF3ZGFoJTIwdW1yYWh8ZW58MHx8fHwxNzkwOTI1OTYwfDA&ixlib=rb-4.1.0&q=85";

const inputClass =
  "w-full rounded-xl bg-[#0A0F1D] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-400/60 focus:ring-1 focus:ring-sky-400/40 transition-colors duration-200";

const INITIAL = {
  nama_travel: "",
  penanggung_jawab: "",
  whatsapp: "",
  jumlah_jamaah: "",
  tanggal_keberangkatan: "",
  catatan: "",
};

export const OrderForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/orders", {
        nama_travel: form.nama_travel.trim(),
        penanggung_jawab: form.penanggung_jawab.trim(),
        whatsapp: form.whatsapp.trim(),
        jumlah_jamaah: Number(form.jumlah_jamaah),
        tanggal_keberangkatan: form.tanggal_keberangkatan || null,
        catatan: form.catatan.trim() || null,
      });
      toast.success("Permintaan booking terkirim! Admin akan menghubungi Anda via WhatsApp.");
      setForm(INITIAL);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontak" className="relative py-20 lg:py-28 bg-[#0A0F1D] border-t border-white/8 overflow-hidden" data-testid="order-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-white/8">
              <img src={RAWDAH_IMG} alt="Masjid Nabawi Madinah" className="w-full h-56 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1D] to-transparent" />
            </div>
            <p className="mt-8 font-mono2 text-xs uppercase tracking-[0.3em] text-sky-400">Booking Sekarang</p>
            <h2 className="mt-4 font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-white" data-testid="order-title">
              Kirim Data, Kami Bookingkan Permitnya
            </h2>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">
              Isi form pemesanan atau hubungi admin langsung. Respon cepat, proses transparan, permit terbit sebelum
              jamaah Anda berangkat ke Madinah.
            </p>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="order-whatsapp-card"
              className="mt-6 flex items-center gap-4 glass rounded-2xl p-5 hover:border-emerald-400/40 transition-colors duration-300"
            >
              <span className="flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-400/30">
                <Phone size={20} className="text-emerald-400" />
              </span>
              <span>
                <span className="block text-xs text-slate-400">Admin Fast Response (WhatsApp)</span>
                <span className="block font-mono2 font-semibold text-xl text-white mt-0.5">{WA_NUMBER}</span>
              </span>
            </a>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.12}>
            <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8 space-y-5" data-testid="order-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-2" htmlFor="nama_travel">Nama Travel Umroh</label>
                  <input id="nama_travel" required minLength={2} value={form.nama_travel} onChange={set("nama_travel")}
                    placeholder="PT. Contoh Travel" className={inputClass} data-testid="order-travel-name-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-2" htmlFor="pic">Nama Penanggung Jawab</label>
                  <input id="pic" required minLength={2} value={form.penanggung_jawab} onChange={set("penanggung_jawab")}
                    placeholder="Nama lengkap" className={inputClass} data-testid="order-pic-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-2" htmlFor="wa">No WhatsApp Aktif</label>
                  <input id="wa" required minLength={8} value={form.whatsapp} onChange={set("whatsapp")}
                    placeholder="08xxxxxxxxxx" className={inputClass} data-testid="order-whatsapp-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-2" htmlFor="jml">Jumlah Jamaah</label>
                  <input id="jml" required type="number" min={1} max={10000} value={form.jumlah_jamaah} onChange={set("jumlah_jamaah")}
                    placeholder="Contoh: 45" className={inputClass} data-testid="order-jamaah-input" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-2" htmlFor="tgl">Tanggal Keberangkatan (opsional)</label>
                <input id="tgl" type="date" value={form.tanggal_keberangkatan} onChange={set("tanggal_keberangkatan")}
                  className={inputClass} data-testid="order-date-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-2" htmlFor="catatan">Catatan (opsional)</label>
                <textarea id="catatan" rows={3} value={form.catatan} onChange={set("catatan")}
                  placeholder="Contoh: preferensi waktu ziarah, jadwal tiba di Madinah, dll."
                  className={`${inputClass} resize-none`} data-testid="order-notes-input" />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="order-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-semibold text-white bg-gradient-to-br from-sky-500 to-sky-700 hover:from-sky-400 hover:to-sky-600 shadow-lg shadow-sky-950/50 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
                {loading ? "Mengirim..." : "Kirim Permintaan Booking"}
              </button>
              <p className="text-xs text-slate-500 text-center">
                Data Anda aman — hanya digunakan untuk proses booking permit Rawdah.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
